/** SIP.js stays behind this boundary. Every command targets one call (ADR 0004). */
import { Invitation, type Session } from "sip.js";
import { callNumber, incomingCallNumber } from "./callNumber";
import {
  SessionManager,
  defaultSessionDescriptionHandlerFactory,
  type SessionDescriptionHandler,
  type SessionDescriptionHandlerOptions,
} from "sip.js/lib/platform/web";

export type CallStatus = "ringing" | "dialing" | "answering" | "active" | "holding" | "held" | "resuming" | "paused" | "uncertain" | "ending";
export interface IncomingInvite { fromNumber: string | null }
export interface SoftphoneCall extends IncomingInvite {
  id: string;
  direction: "incoming" | "outgoing";
  state: CallStatus;
  muted: boolean;
}
export interface SoftphoneSnapshot {
  calls: SoftphoneCall[];
  activeCallId: string | null;
  busy: boolean;
}
export interface SoftphoneUser {
  connect(): Promise<void>;
  register(): Promise<void>;
  unregister(): Promise<void>;
  disconnect(): Promise<void>;
  call(target: string): Promise<void>;
  answer(id: string): Promise<void>;
  hangup(id: string): Promise<void>;
  hold(id: string): Promise<void>;
  resume(id: string): Promise<void>;
  mute(muted: boolean): void;
  sendDTMF(digit: string): Promise<void>;
}
export interface CreateSoftphoneUserParams {
  wssUri: string;
  server: string;
  username: string;
  password: string;
  displayName?: string | null;
  delegate: {
    onCallsChanged(snapshot: SoftphoneSnapshot): void;
    onRegistered?(): void;
    onUnregistered?(): void;
    onServerConnect?(): void;
    onServerDisconnect?(): void;
  };
}
interface CallEntry { session: Session; call: SoftphoneCall }
interface HoldRequest {
  held: boolean;
  resolve(): void;
  reject(error: Error): void;
  timer: ReturnType<typeof setTimeout>;
}
const MAX_CALLS = 5;
const HOLD_TIMEOUT_MS = 10000;
let activeUser: SoftphoneUser | null = null;

function disposeActiveUser(): void {
  const user = activeUser;
  activeUser = null;
  if (user) void user.disconnect().catch(() => {});
}
if (module.hot) module.hot.dispose(disposeActiveUser);

export function createSoftphoneUser(params: CreateSoftphoneUserParams): SoftphoneUser {
  disposeActiveUser();
  const user = new BrowserSoftphone(params);
  activeUser = user;
  return user;
}

class BrowserSoftphone implements SoftphoneUser {
  private manager: SessionManager;
  private calls = new Map<string, CallEntry>();
  private audio = new Map<string, HTMLAudioElement>();
  private handlers = new Map<string, SessionDescriptionHandler>();
  private streams = new Map<string, Set<MediaStream>>();
  private holds = new Map<string, HoldRequest>();
  private activeId: string | null = null;
  private busy = false;
  private disposed = false;
  private generation = 0;

  constructor(private params: CreateSoftphoneUserParams) {
    this.manager = new SessionManager(params.wssUri, {
      aor: `sip:${params.username}@${params.server}`,
      maxSimultaneousSessions: MAX_CALLS,
      reconnectionAttempts: 10,
      reconnectionDelay: 4,
      media: {
        constraints: { audio: true, video: false },
        remote: (session) => {
          let audio = this.audio.get(session.id);
          if (!audio) {
            audio = document.createElement("audio");
            audio.autoplay = true;
            audio.muted = true;
            this.audio.set(session.id, audio);
          }
          return { audio };
        },
      },
      userAgentOptions: {
        authorizationUsername: params.username,
        authorizationPassword: params.password,
        displayName: params.displayName ?? undefined,
        transportOptions: { server: params.wssUri, keepAliveInterval: 30 },
        sessionDescriptionHandlerFactoryOptions: {
          iceGatheringTimeout: 1000,
          peerConnectionConfiguration: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
        },
        sessionDescriptionHandlerFactory: (session, options) => {
          // Gate tracks BEFORE they enter the PeerConnection, including replacements
          // during re-INVITEs. A new getUserMedia stream otherwise starts enabled.
          const factory = defaultSessionDescriptionHandlerFactory(async (constraints) => {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            stream.getTracks().forEach((track) => { track.enabled = this.maySend(session.id); });
            if (this.disposed || !this.calls.has(session.id)) {
              stream.getTracks().forEach((track) => track.stop());
              throw new Error("Appel terminé pendant l’accès au microphone");
            }
            const streams = this.streams.get(session.id) ?? new Set<MediaStream>();
            streams.add(stream);
            this.streams.set(session.id, streams);
            return stream;
          });
          const handler = factory(session, options) as SessionDescriptionHandler;
          const enableSenderTracks = handler.enableSenderTracks.bind(handler);
          // SIP.js may optimistically enable tracks and re-enable them on rejection.
          // The app's permission remains authoritative in every one of those paths.
          handler.enableSenderTracks = (enabled) => enableSenderTracks(enabled && this.maySend(session.id));
          this.handlers.set(session.id, handler);
          return handler;
        },
      },
      delegate: {
        onCallCreated: (session) => this.created(session),
        onCallReceived: (session) => {
          if (!this.calls.has(session.id)) {
            void (session as Invitation).reject({ statusCode: 486 }).catch(() => {});
          }
        },
        onCallAnswered: (session) => {
          const entry = this.calls.get(session.id);
          if (this.disposed || !entry) return;
          const expected = this.activeId === session.id && ["answering", "dialing"].includes(entry.call.state);
          entry.call.state = expected ? "active" : "paused";
          this.updateMedia(entry);
          this.publish();
        },
        onCallHangup: (session) => this.ended(session.id),
        onCallHold: (session, held) => this.holdConfirmed(session.id, held),
        onRegistered: () => { if (!this.disposed) params.delegate.onRegistered?.(); },
        onUnregistered: () => { if (!this.disposed) params.delegate.onUnregistered?.(); },
        onServerConnect: () => { if (!this.disposed) params.delegate.onServerConnect?.(); },
        onServerDisconnect: () => {
          this.clearCalls();
          if (!this.disposed) params.delegate.onServerDisconnect?.();
        },
      },
    });
  }

  private created(session: Session): void {
    if (this.disposed || this.calls.size >= MAX_CALLS) return;
    const incoming = session instanceof Invitation;
    const identity = session.remoteIdentity;
    const request = incoming ? (session as Invitation).request : undefined;
    const call: SoftphoneCall = {
      id: session.id, direction: incoming ? "incoming" : "outgoing",
      fromNumber: incoming ? incomingCallNumber({
        user: identity.uri.user, displayName: identity.displayName,
        assertedIdentity: request?.getHeader("P-Asserted-Identity"),
        remotePartyId: request?.getHeader("Remote-Party-ID"),
        privacy: request?.getHeader("Privacy"),
      }) : callNumber(identity.uri.user),
      state: incoming ? "ringing" : "dialing", muted: false,
    };
    this.calls.set(session.id, { session, call });
    if (!incoming && !this.activeId) this.activeId = session.id;
    this.publish();
  }

  private maySend(id: string): boolean {
    const call = this.calls.get(id)?.call;
    return !this.disposed && this.activeId === id && call?.state === "active" && !call.muted;
  }

  private updateMedia(entry: CallEntry): void {
    const audible = !this.disposed && this.activeId === entry.call.id && entry.call.state === "active";
    const audio = this.audio.get(entry.call.id);
    if (audio) audio.muted = !audible;
    // Keep SIP.js's managed mute flag closed too, so a later hold rejection
    // cannot reopen a background session. The user's mute preference is separate.
    if (entry.session.state === "Established") {
      if (this.maySend(entry.call.id)) this.manager.unmute(entry.session);
      else this.manager.mute(entry.session);
    }
    this.streams.get(entry.call.id)?.forEach((stream) => {
      stream.getTracks().forEach((track) => { track.enabled = this.maySend(entry.call.id); });
    });
  }

  private publish(): void {
    this.params.delegate.onCallsChanged({
      calls: Array.from(this.calls.values(), ({ call }) => ({ ...call })),
      activeCallId: this.activeId, busy: this.busy || this.hasUncertainCall(),
    });
  }

  private requireCall(id: string): CallEntry {
    const entry = this.calls.get(id);
    if (this.disposed || !entry || entry.call.state === "ending" || ["Terminating", "Terminated"].includes(entry.session.state)) {
      throw new Error("Cet appel est terminé");
    }
    return entry;
  }

  private hasUncertainCall(): boolean {
    return this.holds.size > 0 || Array.from(this.calls.values()).some(({ call }) => call.state === "uncertain");
  }

  private async exclusive(action: () => Promise<void>): Promise<void> {
    if (this.disposed) throw new Error("Téléphone déconnecté");
    if (this.busy) throw new Error("Une opération d’appel est déjà en cours");
    if (this.hasUncertainCall()) throw new Error("Attendez la confirmation de l’appel ou raccrochez-le avant de continuer");
    this.busy = true;
    this.publish();
    try { await action(); }
    finally { this.busy = false; this.publish(); }
  }

  private holdConfirmed(id: string, held: boolean): void {
    const entry = this.calls.get(id);
    const pending = this.holds.get(id);
    if (this.disposed || !entry || !pending) return;
    clearTimeout(pending.timer);
    this.holds.delete(id);
    // Even on successful unhold the microphone stays closed until the command
    // continuation explicitly selects the call. Late responses never select it.
    this.restoreHoldPolicy(entry, held);
    entry.call.state = held ? "held" : "paused";
    this.updateMedia(entry);
    this.publish();
    if (held === pending.held) pending.resolve();
    else pending.reject(new Error(held ? "La reprise de l’appel a été refusée" : "La mise en attente a été refusée"));
  }

  private restoreHoldPolicy(entry: CallEntry, held: boolean): void {
    // SessionManager rolls back its flag after rejection, but leaves this SDP
    // option stale. Future PBX re-INVITEs must use the confirmed direction.
    const options: SessionDescriptionHandlerOptions = {
      ...entry.session.sessionDescriptionHandlerOptionsReInvite, hold: held,
    };
    entry.session.sessionDescriptionHandlerOptionsReInvite = options;
  }

  private changeHold(entry: CallEntry, held: boolean): Promise<void> {
    const id = entry.call.id;
    if (this.holds.has(id)) return Promise.reject(new Error("La confirmation de cet appel est encore en attente"));
    entry.call.state = held ? "holding" : "resuming";
    if (this.activeId === id) this.activeId = null;
    this.updateMedia(entry);
    this.publish();
    return new Promise<void>((resolve, reject) => {
      const pending: HoldRequest = {
        held, resolve, reject,
        timer: setTimeout(() => {
          if (this.holds.get(id) !== pending) return;
          entry.call.state = "uncertain";
          this.updateMedia(entry);
          this.publish();
          // Keep the entry until the late SIP response: no conflicting re-INVITE.
          reject(new Error("Le téléphone n’a pas confirmé l’opération. Le micro reste coupé."));
        }, HOLD_TIMEOUT_MS),
      };
      this.holds.set(id, pending);
      const sent = Promise.resolve().then(() => {
        if (this.holds.get(id) !== pending) return;
        return held ? this.manager.hold(entry.session) : this.manager.unhold(entry.session);
      });
      void sent.catch((error) => {
        if (this.holds.get(id) !== pending) return;
        clearTimeout(pending.timer);
        this.holds.delete(id);
        const heldNow = this.manager.isHeld(entry.session);
        this.restoreHoldPolicy(entry, heldNow);
        entry.call.state = heldNow ? "held" : "paused";
        this.updateMedia(entry);
        this.publish();
        reject(error);
      });
    });
  }

  private async holdCurrent(exceptId?: string): Promise<void> {
    if (this.activeId && this.activeId !== exceptId) {
      const current = this.requireCall(this.activeId);
      if (current.call.state !== "active") throw new Error("Terminez l’appel en cours de connexion avant de répondre");
    }
    // A rejected hold leaves an established but locally silenced call. It still
    // needs confirmed SIP hold before another conversation may start.
    for (const entry of Array.from(this.calls.values())) {
      if (entry.call.id !== exceptId && ["active", "paused"].includes(entry.call.state)) {
        if (entry.session.state !== "Established") throw new Error("Cet appel doit être terminé avant de continuer");
        await this.changeHold(entry, true);
      }
    }
  }

  connect(): Promise<void> { return this.manager.connect(); }
  register(): Promise<void> { return this.manager.register(); }
  unregister(): Promise<void> { return this.manager.unregister(); }
  async disconnect(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.clearCalls();
    await this.manager.disconnect();
  }

  async call(target: string): Promise<void> {
    return this.exclusive(async () => {
      if (this.activeId || Array.from(this.calls.values()).some(({ call }) => call.state === "paused")) throw new Error("Mettez d’abord l’appel en cours en attente");
      if (this.calls.size >= MAX_CALLS) throw new Error("La file contient déjà cinq appels");
      const generation = this.generation;
      try { await this.manager.call(target); }
      catch (error) {
        const dialing = this.activeId && this.calls.get(this.activeId);
        if (dialing && dialing.call.state === "dialing") {
          void this.manager.hangup(dialing.session).catch(() => {});
          this.ended(dialing.call.id);
        }
        throw error;
      }
      if (generation !== this.generation || this.disposed) throw new Error("Téléphone déconnecté");
    });
  }

  async answer(id: string): Promise<void> {
    return this.exclusive(async () => {
      if (this.requireCall(id).call.state !== "ringing") throw new Error("Cet appel ne sonne plus");
      await this.holdCurrent(id);
      const entry = this.requireCall(id);
      this.activeId = id;
      entry.call.state = "answering";
      this.publish();
      try {
        await this.manager.answer(entry.session);
        this.requireCall(id);
      } catch (error) {
        if (this.activeId === id) this.activeId = null;
        if (this.calls.has(id)) {
          entry.call.state = entry.session.state === "Initial" ? "ringing" : "paused";
          this.updateMedia(entry);
          this.publish();
        }
        throw error;
      }
    });
  }

  async hold(id: string): Promise<void> {
    return this.exclusive(async () => {
      const entry = this.requireCall(id);
      if (entry.call.state !== "active" || this.activeId !== id) throw new Error("Cet appel n’est pas actif");
      await this.changeHold(entry, true);
    });
  }

  async resume(id: string): Promise<void> {
    return this.exclusive(async () => {
      const entry = this.requireCall(id);
      if (!["held", "paused"].includes(entry.call.state) || this.holds.has(id)) {
        throw new Error("Cet appel ne peut pas encore être repris");
      }
      await this.holdCurrent(id);
      this.requireCall(id);
      if (this.manager.isHeld(entry.session)) await this.changeHold(entry, false);
      this.requireCall(id);
      this.activeId = id;
      entry.call.state = "active";
      this.updateMedia(entry);
      this.publish();
    });
  }

  async hangup(id: string): Promise<void> {
    const entry = this.requireCall(id);
    const previous = entry.call.state;
    const pending = this.holds.get(id);
    if (pending) {
      clearTimeout(pending.timer);
      pending.reject(new Error("L’appel est en cours de raccrochage"));
      this.holds.delete(id);
    }
    entry.call.state = "ending";
    if (this.activeId === id) this.activeId = null;
    this.updateMedia(entry);
    this.publish();
    try { await this.manager.hangup(entry.session); }
    catch (error) {
      if (this.calls.has(id)) {
        entry.call.state = previous === "ringing" ? "ringing" : "uncertain";
        this.publish();
      }
      throw error;
    }
  }

  mute(muted: boolean): void {
    if (!this.activeId || this.busy) return;
    const entry = this.requireCall(this.activeId);
    if (entry.call.state !== "active") return;
    entry.call.muted = muted;
    this.updateMedia(entry);
    this.publish();
  }

  async sendDTMF(digit: string): Promise<void> {
    if (!this.activeId || this.busy || !/^[0-9A-D#*]$/.test(digit)) return;
    const entry = this.requireCall(this.activeId);
    if (entry.call.state !== "active") return;
    const sent = this.handlers.get(entry.call.id)?.sendDtmf(digit, { duration: 160, interToneGap: 70 });
    if (!sent) await this.manager.sendDTMF(entry.session, digit);
  }

  private ended(id: string): void {
    const entry = this.calls.get(id);
    if (this.activeId === id) this.activeId = null;
    if (entry) {
      // Disable while the session still exists; then remove its permission.
      entry.call.state = "ending";
      this.updateMedia(entry);
      try { this.manager.getLocalMediaStream(entry.session)?.getTracks().forEach((t) => { t.enabled = false; t.stop(); }); } catch { /* handler already closed */ }
    }
    this.calls.delete(id);
    this.streams.get(id)?.forEach((stream) => stream.getTracks().forEach((t) => { t.enabled = false; t.stop(); }));
    this.streams.delete(id);
    this.handlers.delete(id);
    const audio = this.audio.get(id);
    if (audio) { audio.muted = true; audio.srcObject = null; }
    this.audio.delete(id);
    const pending = this.holds.get(id);
    if (pending) { clearTimeout(pending.timer); pending.reject(new Error("L’appel a été terminé")); this.holds.delete(id); }
    this.publish();
  }

  private clearCalls(): void {
    this.generation++;
    Array.from(this.calls.keys()).forEach((id) => this.ended(id));
    this.activeId = null;
    this.publish();
  }
}

export type MicErrorKind = "micNoDevice" | "micDenied" | "micInUse";

export function classifyMediaError(e: unknown): MicErrorKind | null {
  const name = e instanceof Error ? e.name : "";
  const msg = e instanceof Error ? e.message : String(e ?? "");

  if (
    name === "NotFoundError" ||
    name === "OverconstrainedError" ||
    /requested device not found|device not found/i.test(msg)
  ) {
    return "micNoDevice";
  }
  if (name === "NotAllowedError" || name === "SecurityError" || /permission|not allowed|denied/i.test(msg)) {
    return "micDenied";
  }
  if (
    name === "NotReadableError" ||
    name === "AbortError" ||
    /could not start|in use|trackstart/i.test(msg)
  ) {
    return "micInUse";
  }
  return null;
}

export function buildCallTarget(server: string, number: string): string {
  const target = callNumber(number);
  if (!target) throw new Error("Numéro de téléphone invalide");
  return `sip:${target}@${server}`;
}
